import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/date";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Warning, ShieldWarning, PencilSimple, Prohibit } from "@phosphor-icons/react";

const BLANK = {
  reason: "", permitted_use: "", basis_source: "", authorisation_required: true,
  authorised: true, review_date: "", review_trigger: "", notes: "",
};

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[11px] uppercase font-bold tracking-wide text-amber-800/70">{label}</div>
      <div className="text-sm font-medium text-slate-800 whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function RestrictionForm({ id, existing, onClose, onDone }) {
  const correcting = !!existing;
  const [f, setF] = useState(existing ? {
    reason: existing.reason || "", permitted_use: existing.permitted_use || "",
    basis_source: existing.basis_source || "", authorisation_required: existing.authorisation_required !== false,
    authorised: false, review_date: existing.review_date || "", review_trigger: existing.review_trigger || "",
    notes: existing.notes || "",
  } : { ...BLANK });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.reason.trim()) { toast.error("A restriction reason is required"); return; }
    setBusy(true);
    try {
      const url = correcting
        ? `/assets/${id}/restrictions/${existing.id}/correct`
        : `/assets/${id}/restrictions`;
      await api.post(url, f);
      toast.success(correcting ? "Restriction corrected (history preserved)" : "Restricted use authorised");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="restriction-form" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{correcting ? "Correct restriction" : "Authorise restricted use (Amber)"}</DialogTitle>
          <DialogDescription>
            Amber is only valid with a documented basis. If no valid basis exists the asset is treated as Do Not Use.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Restriction reason *</label>
            <Textarea data-testid="restriction-reason" value={f.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Why is use restricted?" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Permitted use / operating conditions</label>
            <Textarea data-testid="restriction-permitted-use" value={f.permitted_use} onChange={(e) => set("permitted_use", e.target.value)} placeholder="What use is allowed, and under what conditions?" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Source / basis of restriction</label>
            <Input data-testid="restriction-basis-source" value={f.basis_source} onChange={(e) => set("basis_source", e.target.value)} placeholder="e.g. Engineer's report, risk assessment ref…" className="mt-1 h-9" />
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" data-testid="restriction-auth-required" checked={f.authorisation_required} onChange={(e) => set("authorisation_required", e.target.checked)} />
            Authorisation required for continued use
          </label>
          {f.authorisation_required && (
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 ml-6">
              <input type="checkbox" data-testid="restriction-authorise-now" checked={f.authorised} onChange={(e) => set("authorised", e.target.checked)} />
              I authorise continued use (records me as the authoriser)
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500">Review / expiry date</label>
              <Input type="date" data-testid="restriction-review-date" value={f.review_date} onChange={(e) => set("review_date", e.target.value)} className="mt-1 h-9" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500">Review trigger</label>
              <Input data-testid="restriction-review-trigger" value={f.review_trigger} onChange={(e) => set("review_trigger", e.target.value)} placeholder="e.g. after repair" className="mt-1 h-9" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Notes / evidence</label>
            <Textarea data-testid="restriction-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} data-testid="restriction-cancel">Cancel</Button>
            <Button data-testid="restriction-submit" disabled={busy} onClick={submit} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
              {correcting ? "Save correction" : "Authorise"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LiftDialog({ id, restriction, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required to lift a restriction"); return; }
    setBusy(true);
    try {
      await api.post(`/assets/${id}/restrictions/${restriction.id}/lift`, { reason });
      toast.success("Restriction lifted"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="restriction-lift-dialog">
        <DialogHeader>
          <DialogTitle>Lift restriction</DialogTitle>
          <DialogDescription>The restriction is retained in history. Any compliance lock or other hold still applies.</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="restriction-lift-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for lifting (e.g. repaired & returned to service)…" />
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="restriction-lift-submit" disabled={busy} onClick={submit} className="bg-slate-900 font-bold">Lift restriction</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RestrictionPanel({ asset, canAuthorise, onChange }) {
  const id = asset.id;
  const eff = asset.effective_status || {};
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [correct, setCorrect] = useState(null);
  const [lift, setLift] = useState(null);

  const load = useCallback(() => {
    api.get(`/assets/${id}/restrictions`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const active = (items || []).find((r) => r.state === "active");
  const history = (items || []).filter((r) => r.state !== "active");
  const missing = !!eff.restriction_missing; // recorded restricted but no valid basis -> Do Not Use

  // Nothing to show and can't act
  if (!active && !missing && !canAuthorise) return null;
  if (items && items.length === 0 && !missing && !canAuthorise) return null;

  const refresh = () => { load(); onChange && onChange(); setShowForm(false); setCorrect(null); setLift(null); };

  return (
    <div data-testid="restriction-panel" className="rounded-lg border-2 border-amber-400 bg-amber-50/60 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-amber-400/80">
        <div className="flex items-center gap-2 font-display font-black uppercase tracking-wide text-slate-900 text-sm">
          <ShieldWarning size={18} weight="bold" /> Restricted Use — Documented Basis
        </div>
        {canAuthorise && !active && (
          <Button data-testid="restriction-add" onClick={() => setShowForm(true)} className="h-8 bg-slate-900 text-white font-bold text-xs">
            Authorise restricted use
          </Button>
        )}
      </div>

      <div className="p-4 flex flex-col gap-3">
        {missing && (
          <div data-testid="restriction-missing" className="flex items-start gap-2 rounded bg-red-600 text-white px-3 py-2 text-sm font-semibold">
            <Prohibit size={18} weight="bold" className="mt-0.5 shrink-0" />
            This asset is recorded as Restricted but has no valid authorised basis for continued use — it is treated as Do Not Use until a documented restriction is authorised.
          </div>
        )}

        {active ? (
          <div data-testid="restriction-active" className="flex flex-col gap-2.5">
            {!active.valid && (
              <div className="flex items-center gap-2 text-xs font-bold text-red-700">
                <Warning size={15} weight="bold" />
                {active.expired ? "This basis has passed its review/expiry date." : active.needs_authorisation ? "Awaiting authorisation — not yet a valid basis." : ""}
              </div>
            )}
            {active.needs_review && (
              <div data-testid="restriction-needs-review" className="text-xs font-bold text-amber-800 bg-amber-100 rounded px-2 py-1">
                Review required — complete a proper documented basis for this pre-existing restriction.
              </div>
            )}
            <Field label="Restriction reason" value={active.reason} />
            <Field label="Permitted use / conditions" value={active.permitted_use} />
            <Field label="Source / basis" value={active.basis_source} />
            <Field label="Authorised by" value={active.authorised_at ? `${active.authorised_by_name || "—"} · ${fmtDateTime(active.authorised_at)}` : (active.authorisation_required ? "Not yet authorised" : "Authorisation not required")} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Effective from" value={active.effective_from ? fmtDate(active.effective_from) : null} />
              <Field label="Review / expiry" value={active.review_date ? fmtDate(active.review_date) : (active.review_trigger || null)} />
            </div>
            <Field label="Notes" value={active.notes} />
            {canAuthorise && (
              <div className="flex gap-2 pt-1">
                <Button data-testid="restriction-correct" onClick={() => setCorrect(active)} variant="outline" className="h-8 text-xs font-semibold">
                  <PencilSimple size={14} weight="bold" className="mr-1" />Correct
                </Button>
                <Button data-testid="restriction-lift" onClick={() => setLift(active)} className="h-8 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white">
                  Lift restriction
                </Button>
              </div>
            )}
          </div>
        ) : (
          !missing && <p className="text-sm text-slate-500">No active restriction on this asset.</p>
        )}

        {history.length > 0 && (
          <details data-testid="restriction-history" className="mt-1">
            <summary className="text-xs font-bold uppercase text-slate-500 cursor-pointer">History ({history.length})</summary>
            <div className="mt-2 flex flex-col gap-2">
              {history.map((r) => (
                <div key={r.id} className="text-xs text-slate-600 border-l-2 border-slate-300 pl-2">
                  <span className="font-bold uppercase">{r.state}</span> · {r.reason}
                  {r.created_at ? ` · ${fmtDate(r.created_at)}` : ""}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {showForm && <RestrictionForm id={id} onClose={() => setShowForm(false)} onDone={refresh} />}
      {correct && <RestrictionForm id={id} existing={correct} onClose={() => setCorrect(null)} onDone={refresh} />}
      {lift && <LiftDialog id={id} restriction={lift} onClose={() => setLift(null)} onDone={refresh} />}
    </div>
  );
}
